import React, { useState, useMemo, useCallback } from 'react';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import AddIcon from '@mui/icons-material/Add';
import { CategoryFilteredSpellList } from '../shared/CategoryFilteredSpellList';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { createIceSurface } from '../../theme/surfaces';
import Button from '@mui/material/Button';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { SectionHeading } from '../shared/SectionHeading';
import { WarbandMemberCard } from '../shared/WarbandMemberCard';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { useItemPopover } from '../../hooks/useItemPopover';
import { gridFlexSx } from '../../App';
import { getSplitStatValue } from '../../utils/splitStats';
import { computeLevelUpStatBonuses } from '../../utils/formatting';
import { SoldierDTO } from '../../types';

const NIGGLE_ID = 'injury_niggle';
const FAMILIAR_BUFF_ID = 'spell_familiar';
const FAMILIAR_SPELL_ID = 'familiar';

export const PreGamePage: React.FC = () => {
  const { navigateToPage, wizardData, apprenticeData, captainData, spellcastersAndSpells, selectedWarbandId, warbandBuffs, soldiersData, openCreateSoldierModal } = useWarbandStore();
  const updateMemberBuffs = useDirtyTrackingStore((s) => s.updateMemberBuffs);
  const updateMemberHealth = useDirtyTrackingStore((s) => s.updateMemberHealth);
  const queueSoldierDelete = useDirtyTrackingStore((s) => s.queueSoldierDelete);

  const [isPlayConfirmOpen, setIsPlayConfirmOpen] = useState(false);
  const [wizardNiggleActive, setWizardNiggleActive] = useState(false);
  const [apprenticeNiggleActive, setApprenticeNiggleActive] = useState(false);
  const [captainNiggleActive, setCaptainNiggleActive] = useState(false);
  const [pendingRemoveSoldier, setPendingRemoveSoldier] = useState<SoldierDTO | null>(null);

  const levelUps = useWarbandStore((s) => s.levelUps);
  const levelUpBonuses = useMemo(() => computeLevelUpStatBonuses(levelUps), [levelUps]);
  const [wizardFamiliarActive, setWizardFamiliarActive] = useState(false);
  const [apprenticeFamiliarActive, setApprenticeFamiliarActive] = useState(false);

  const { popover, handleItemClick, closePopover } = useItemPopover();

  const temporarySoldiers = useMemo(
    () => soldiersData?.soldiers.filter((s) => s.isTemporary) ?? [],
    [soldiersData]
  );

  const wizardHasNiggle = wizardData?.permanentInjuries?.some(i => i.id === NIGGLE_ID) ?? false;
  const apprenticeHasNiggle = apprenticeData?.permanentInjuries?.some(i => i.id === NIGGLE_ID) ?? false;
  const captainHasNiggle = captainData?.permanentInjuries?.some(i => i.id === NIGGLE_ID) ?? false;
  const showNiggleSection = wizardHasNiggle || apprenticeHasNiggle || captainHasNiggle;

  const warbandHasFamiliar = spellcastersAndSpells?.warbandSpells.some(s => s.id === FAMILIAR_SPELL_ID) ?? false;

  const toggleBuff = (
    buffId: string,
    memberId: string,
    role: 'wizard' | 'apprentice' | 'captain',
    member: typeof wizardData,
    isActive: boolean,
    setActive: (v: boolean) => void
  ) => {
    if (!member) return;
    const existingIds = member.buffsAndDebuffs?.flatMap(b => Array(b.stackCount ?? 1).fill(b.id)) ?? [];
    const baseIds = existingIds.filter(id => id !== buffId);
    const newIds = isActive ? baseIds : [...baseIds, buffId];
    updateMemberBuffs(memberId, role, newIds);
    setActive(!isActive);
  };

  const toggleNiggle = (
    memberId: string,
    role: 'wizard' | 'apprentice' | 'captain',
    member: typeof wizardData,
    isActive: boolean,
    setActive: (v: boolean) => void
  ) => {
    if (!member) return;
    toggleBuff(NIGGLE_ID, memberId, role, member, isActive, setActive);
  };

  const handleRemoveSoldier = useCallback((soldier: SoldierDTO) => {
    setPendingRemoveSoldier(soldier);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0, overflowY: 'auto', pt: 2, pb: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(300, { icicles: true, card: true }) }} p={1.5}>
        <CategoryFilteredSpellList categoryIds={["out_of_game_b"]} label="Out Of Game (B) Spells" />
      </Box>

      {showNiggleSection && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...createIceSurface(140, { icicles: true, card: true }) }} p={1.5}>
          <SectionHeading label="Niggling Injuries" />
          <Typography>  
            Please select any characters that have active Niggling Inuries this game.
          </Typography>

          <ToggleButtonGroup size="small" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {wizardHasNiggle && wizardData && (
              <ToggleButton
                value="wizard"
                selected={wizardNiggleActive}
                onClick={() => toggleNiggle(wizardData.id, 'wizard', wizardData, wizardNiggleActive, setWizardNiggleActive)}
              >
                {wizardData.name} (Wizard)
              </ToggleButton>
            )}
            {apprenticeHasNiggle && apprenticeData && (
              <ToggleButton
                value="apprentice"
                selected={apprenticeNiggleActive}
                onClick={() => toggleNiggle(apprenticeData.id, 'apprentice', apprenticeData, apprenticeNiggleActive, setApprenticeNiggleActive)}
              >
                {apprenticeData.name} (Apprentice)
              </ToggleButton>
            )}
            {captainHasNiggle && captainData && (
              <ToggleButton
                value="captain"
                selected={captainNiggleActive}
                onClick={() => toggleNiggle(captainData.id, 'captain', captainData, captainNiggleActive, setCaptainNiggleActive)}
              >
                {captainData.name} (Captain)
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
      )}

      {warbandHasFamiliar && (wizardData || apprenticeData) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...createIceSurface(140, { icicles: true, card: true }) }} p={1.5}>
          <SectionHeading label="Familiar" />
          <Typography>  
            Please select any characters that have successfully cast Familiar this game.
          </Typography>          
          <ToggleButtonGroup size="small" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {wizardData && (
              <ToggleButton
                value="wizard"
                selected={wizardFamiliarActive}
                onClick={() => toggleBuff(FAMILIAR_BUFF_ID, wizardData.id, 'wizard', wizardData, wizardFamiliarActive, setWizardFamiliarActive)}
              >
                {wizardData.name} (Wizard)
              </ToggleButton>
            )}
            {apprenticeData && (
              <ToggleButton
                value="apprentice"
                selected={apprenticeFamiliarActive}
                onClick={() => toggleBuff(FAMILIAR_BUFF_ID, apprenticeData.id, 'apprentice', apprenticeData, apprenticeFamiliarActive, setApprenticeFamiliarActive)}
              >
                {apprenticeData.name} (Apprentice)
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...createIceSurface(200, { icicles: true, card: true }) }} p={1.5}>
        <SectionHeading
          label="Temporary Soldiers"
          action={
            <IconButton size="small" onClick={() => openCreateSoldierModal(true)}>
              <AddIcon />
            </IconButton>
          }
        />
        <Box sx={gridFlexSx}>
          {temporarySoldiers.map((soldier) => (
            <WarbandMemberCard
              key={soldier.id}
              member={soldier}
              isDefeated={false}
              onItemClick={handleItemClick}
              showArmourWhenEmpty={false}
              editMode
              titleMenuItems={[{ label: 'Remove', onClick: () => handleRemoveSoldier(soldier) }]}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(42, { icicles: true, card: true }) }} p={1.5}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SportsKabaddiIcon />}
          onClick={() => setIsPlayConfirmOpen(true)}
        >
          Start the game!
        </Button>
      </Box>
      <ConfirmActionDialog
        open={isPlayConfirmOpen}
        title="Start the game?"
        message={`Are you sure you're ready to start the game? If you change your mind, you can always return to the pre-game setup later, via the menu at the top right.`}
        confirmLabel="Start"
        confirmColor="primary"
        onClose={() => setIsPlayConfirmOpen(false)}
        onConfirm={() => {
          if (wizardData) {
            const maxHealth = getSplitStatValue(wizardData, 'health', wizardData.health + levelUpBonuses.health, warbandBuffs);
            if (wizardData.currentHealth < maxHealth) {
              updateMemberHealth(wizardData.id, 'wizard', maxHealth);
            }
          }
          if (apprenticeData) {
            const maxHealth = getSplitStatValue(apprenticeData, 'health', apprenticeData.health + levelUpBonuses.health, warbandBuffs);
            if (apprenticeData.currentHealth < maxHealth) {
              updateMemberHealth(apprenticeData.id, 'apprentice', maxHealth);
            }
          }
          setIsPlayConfirmOpen(false);
          navigateToPage("play");
        }}
      />
      <ItemDetailsPopover popover={popover} onClose={closePopover} />
      <ConfirmActionDialog
        open={pendingRemoveSoldier !== null}
        title="Remove soldier?"
        message={`Remove ${pendingRemoveSoldier?.name ?? 'this soldier'} from this party?`}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setPendingRemoveSoldier(null)}
        onConfirm={() => {
          if (pendingRemoveSoldier) {
            queueSoldierDelete(pendingRemoveSoldier.id, pendingRemoveSoldier.costPaid ?? pendingRemoveSoldier.cost, false, false);
          }
          setPendingRemoveSoldier(null);
        }}
      />
    </Box>
  );
};
